import { useCallback } from "react";
import { notification } from "antd";
import { SchemaFormAction } from "../../../../components/forms/FormActionNew/FormAction";
import { Action, FormActionPostValues } from "../../../../types/actions";
import { formatDayJSToDateString } from "../../../../utils/dates";
import { getErrorMessage } from "../../../../utils/errors/errors";
import { useNavigate } from "react-router-dom";
import {
  useActionCreateMutation,
  useActionDeleteMutation,
  useActionUpdateMutation,
  useActionUploadAttachmentsMutation,
} from "../../apiSlice";
import {
  FetchActionsQuery,
  RefetchEventQuery,
} from "../../../../types/reactQueries";

export const useCrudAction = (
  fetchPaginatedActions?: FetchActionsQuery,
  refetchEvent?: RefetchEventQuery,
  eventId?: number,
  isEditMode?: boolean,
  action?: Action,
) => {
  const navigate = useNavigate();

  const [lazyDeleteAction, { isLoading: isDeleting }] =
    useActionDeleteMutation();

  const [actionCreate, { isLoading: isCreating }] = useActionCreateMutation();
  const [updateAction, { isLoading: isUpdating }] = useActionUpdateMutation();
  const [postAttachments, { isLoading: isPostingAttachments }] =
    useActionUploadAttachmentsMutation();

  const _createAction = useCallback(
    async (values: SchemaFormAction, stayToCreateAfterSuccess: boolean) => {
      try {
        console.log("Submit", values);

        // 1) Format values to match the server's expectations
        const formattedValues: FormActionPostValues = {
          ...values,
          date: formatDayJSToDateString(values.date),
          dateAsked: formatDayJSToDateString(values.dateAsked),
          performer: Number(values.performer),
          event: Number(values.event),
        };
        console.log("formattedValues", formattedValues);

        // 2) Create action
        const createdAction = await actionCreate(formattedValues).unwrap();
        console.log("createdAction: ", createdAction);

        // 3) Success notification for action creation
        notification.success({
          message: "Action crÃ©Ã©e",
          description: "L'action a Ã©tÃ© crÃ©Ã©e avec succÃ¨s.",
        });

        const files = values?.attachedFiles;
        if (files?.length) {
          await postAttachments({
            actionId: createdAction.id,
            files,
          });
          // 4) Success notification for files upload
          notification.success({
            message: "PiÃ¨ces jointes ajoutÃ©es",
          });
        }
        if (stayToCreateAfterSuccess) {
          window.location.reload();
        } else {
          navigate(
            `/events/${formattedValues.event}/actions/${createdAction.id}`,
          );
        }
      } catch (error) {
        console.error("Create action error", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [actionCreate, navigate, postAttachments],
  );

  const _updateAction = useCallback(
    async (values: SchemaFormAction) => {
      try {
        if (!action?.id) {
          throw new Error("Action id is missing");
        }

        // 1) Format values to match the server's expectations
        const formattedValues: FormActionPostValues = {
          ...values,
          date: formatDayJSToDateString(values.date),
          dateAsked: formatDayJSToDateString(values.dateAsked),
          performer: Number(values.performer),
          event: Number(values.event),
        };

        console.log("Submit updateAction", values);
        console.log("formattedValues", formattedValues);

        // 2) Update action
        const updatedAction = await updateAction({
          actionId: action.id,
          values: formattedValues,
        }).unwrap();

        console.log("updateAction repsonse: ", updatedAction);

        // 3) Success notification for action creation
        notification.success({
          message: "Action modifiÃ©e",
          description: "L'action a Ã©tÃ© modifiÃ©e avec succÃ¨s.",
        });

        navigate(
          `/events/${formattedValues.event}/actions/${updatedAction.id}`,
        );
      } catch (error) {
        console.error("Update action error", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [action?.id, updateAction, navigate],
  );

  const onDelete = useCallback(
    async (id: number) => {
      try {
        console.log("deleted action id", id);
        await lazyDeleteAction(id).unwrap();
        // Success notification
        notification.success({
          message: "Action supprimÃ©e",
          description: `L'action a Ã©tÃ© supprimÃ©e avec succÃ¨s.`,
        });
        if (fetchPaginatedActions) {
          await fetchPaginatedActions("").unwrap();
        } else if (refetchEvent && eventId) {
          await refetchEvent().unwrap();
        }
      } catch (error) {
        console.error("deleteAction error", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [lazyDeleteAction, fetchPaginatedActions, refetchEvent, eventId],
  );

  return {
    onDelete,
    isDeleting: isDeleting,
    onSubmit: isEditMode ? _updateAction : _createAction,
    isLoading: isCreating || isUpdating,
    isPostingAttachments,
  };
};
